import type { CandidateMatchBand } from '@/lib/candidate-match-bands'
import { matchBandRank } from '@/lib/match-bands'

export type ShortlistInsight = {
  evaluation_id: string
  band: CandidateMatchBand
  /** Integer 0–100 from evaluation final_score. */
  match_percent: number
  highlights: string[]
  red_flags: string[]
  recommendation: string
  /** Narrative summary for the job-page fit widget (falls back to recommendation). */
  summary: string
  source: 'full' | 'listing_description'
  analysed_at: string
}

export type ShortlistWorkbenchItem = {
  insight: ShortlistInsight | null
  savedAt: string
  /** True while fit analysis is running after save. */
  insight_pending?: boolean
}

/**
 * Pending / just-saved first (savedAt desc), then analysed (band rank, analysed_at desc),
 * then other unanalysed by savedAt desc.
 */
export function sortShortlistWorkbenchItems<T extends ShortlistWorkbenchItem>(
  items: T[],
): T[] {
  return [...items].sort((a, b) => {
    const aPending = Boolean(a.insight_pending) && !a.insight
    const bPending = Boolean(b.insight_pending) && !b.insight
    if (aPending !== bPending) {
      return aPending ? -1 : 1
    }
    if (aPending && bPending) {
      return new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime()
    }

    const aAnalysed = Boolean(a.insight)
    const bAnalysed = Boolean(b.insight)
    if (aAnalysed !== bAnalysed) {
      return aAnalysed ? -1 : 1
    }
    if (a.insight && b.insight) {
      const bandDiff = matchBandRank(a.insight.band) - matchBandRank(b.insight.band)
      if (bandDiff !== 0) return bandDiff
      return (
        new Date(b.insight.analysed_at).getTime() - new Date(a.insight.analysed_at).getTime()
      )
    }
    return new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime()
  })
}
